import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { Package, Tag, TrendingUp, Users, Eye, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import AutoLogoutTest from "./AutoLogoutTest";

export default function OverviewDashboard() {
  const { products, loading: productsLoading } = useProducts();
  const { categories, loading: categoriesLoading } = useCategories();
  const [recentProducts, setRecentProducts] = useState<any[]>([]);
  const [recentCategories, setRecentCategories] = useState<any[]>([]);

  useEffect(() => {
    if (products && products.length > 0) {
      // Get 6 most recent products
      const sorted = [...products].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setRecentProducts(sorted.slice(0, 6));
    }
  }, [products]);

  useEffect(() => {
    if (categories && categories.length > 0) {
      // Get 6 most recent categories
      const sorted = [...categories].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setRecentCategories(sorted.slice(0, 6));
    }
  }, [categories]);

  const getTotalValue = () => {
    if (!products) return 0;
    return products.reduce((sum, product) => sum + (product.price || 0), 0);
  };

  const getAverageRating = () => {
    if (!products || products.length === 0) return 0;
    const totalRating = products.reduce((sum, product) => sum + (product.rating || 0), 0);
    return (totalRating / products.length).toFixed(1);
  };

  const getActiveCategories = () => {
    if (!categories) return 0;
    return categories.filter(cat => cat.is_active).length;
  };

  const getInStockProducts = () => {
    if (!products) return 0;
    return products.filter(product => product.in_stock).length;
  };

  if (productsLoading || categoriesLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-600 mt-1">Welcome to your Brutt Admin Dashboard</p>
        </div>
        <div className="flex gap-2">
          <Button asChild>
            <Link to="/products">Manage Products</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/categories">Manage Categories</Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{products?.length || 0}</div>
            <p className="text-xs text-muted-foreground">
              {getInStockProducts()} in stock
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Categories</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories?.length || 0}</div>
            <p className="text-xs text-muted-foreground">
              {getActiveCategories()} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{getTotalValue().toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Average rating: {getAverageRating()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inventory Status</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {products ? Math.round((getInStockProducts() / products.length) * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Products in stock
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Products and Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Products */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Products</CardTitle>
                <CardDescription>
                  Latest products added to your inventory
                </CardDescription>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/products">View All</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentProducts.length > 0 ? (
                recentProducts.map((product) => (
                  <div key={product.id} className="flex items-center space-x-4 p-3 rounded-lg border">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                      <Package className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {product.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        ₹{product.price} • {product.category}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={product.in_stock ? "default" : "secondary"}>
                        {product.in_stock ? "In Stock" : "Out of Stock"}
                      </Badge>
                      {product.rating > 0 && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Eye className="h-4 w-4 mr-1" />
                          {product.rating}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No products yet</p>
                  <Button asChild className="mt-2" size="sm">
                    <Link to="/products">Add First Product</Link>
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Categories */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Categories</CardTitle>
                <CardDescription>
                  Latest categories created
                </CardDescription>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/categories">View All</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentCategories.length > 0 ? (
                recentCategories.map((category) => (
                  <div key={category.id} className="flex items-center space-x-4 p-3 rounded-lg border">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                      <Tag className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {category.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {category.product_count || 0} products
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={category.is_active ? "default" : "secondary"}>
                        {category.is_active ? "Active" : "Inactive"}
                      </Badge>
                      <div className="text-sm text-gray-500">
                        #{category.sort_order || 0}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Tag className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No categories yet</p>
                  <Button asChild className="mt-2" size="sm">
                    <Link to="/categories">Add First Category</Link>
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common tasks to manage your inventory
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button asChild className="h-20 flex-col">
              <Link to="/products">
                <Package className="h-6 w-6 mb-2" />
                <span>Add New Product</span>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-20 flex-col">
              <Link to="/categories">
                <Tag className="h-6 w-6 mb-2" />
                <span>Add New Category</span>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-20 flex-col">
              <Link to="/products">
                <ShoppingCart className="h-6 w-6 mb-2" />
                <span>Manage Inventory</span>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Auto Logout Test Component - Only visible in development */}
      <AutoLogoutTest />
    </div>
  );
} 